import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";

import {
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  ArrowDown,
  ArrowRight,
} from "lucide-react";

import servicesData from "../../data/servicesData";

import "./ServiceDetails.css";

function ServiceDetails() {
  const { serviceSlug } = useParams();

  const service = servicesData[serviceSlug];

  /*
    =========================================================
    SECTION REFS
    =========================================================
  */

  const overviewRef = useRef(null);
  const provideRef = useRef(null);
  const technologyRef = useRef(null);
  const processRef = useRef(null);
  const ctaRef = useRef(null);

  const [visibleSections, setVisibleSections] = useState({
    overview: false,
    provide: false,
    technology: false,
    process: false,
    cta: false,
  });


  /*
    =========================================================
    SCROLL ANIMATION
    =========================================================
  */

  useEffect(() => {
    const sections = [
      {
        ref: overviewRef,
        key: "overview",
      },
      {
        ref: provideRef,
        key: "provide",
      },
      {
        ref: technologyRef,
        key: "technology",
      },
      {
        ref: processRef,
        key: "process",
      },
      {
        ref: ctaRef,
        key: "cta",
      },
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionKey =
              entry.target.dataset.animationSection;

            setVisibleSections((prev) => ({
              ...prev,
              [sectionKey]: true,
            }));

            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    sections.forEach(({ ref, key }) => {
      if (ref.current) {
        ref.current.dataset.animationSection = key;
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);


  /*
    =========================================================
    INVALID SERVICE
    =========================================================
  */

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const Icon = service.icon;


  return (
    <div className="service-details-page">


      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="service-detail-hero">

        {/* Technical background */}

        <div className="service-detail-hero-grid-bg"></div>

        <div className="service-detail-hero-glow service-detail-glow-one"></div>

        <div className="service-detail-hero-glow service-detail-glow-two"></div>


        <div className="service-detail-container">

          {/* Back button */}

          <Link
            to="/services"
            className="back-services"
          >
            <ArrowLeft size={16} />
            Back to Services
          </Link>


          <div className="service-detail-hero-grid">


            {/* =================================================
                HERO CONTENT
            ================================================= */}

            <div className="service-detail-hero-content">

              <div className="service-detail-number">
                {service.number} / 08
              </div>


              <div className="service-detail-icon">

                <Icon
                  size={28}
                  strokeWidth={1.7}
                />

              </div>


              <span className="service-detail-label">
                NEXIS FOLD · {service.shortTitle}
              </span>


              <h1>
                {service.title}
              </h1>


              <p>
                {service.description}
              </p>


              <Link
                to="/contact"
                className="service-primary-button"
              >
                Start a Project
                <ArrowUpRight size={17} />
              </Link>

            </div>


            {/* =================================================
                HERO VISUAL
            ================================================= */}

            <div className="service-hero-visual">

              <div className="service-visual-box">

                <div className="service-visual-grid"></div>

                <div className="service-visual-glow"></div>


                <div className="service-visual-corner service-visual-corner-tl"></div>

                <div className="service-visual-corner service-visual-corner-tr"></div>

                <div className="service-visual-corner service-visual-corner-bl"></div>

                <div className="service-visual-corner service-visual-corner-br"></div>


                <div className="service-visual-top">

                  <span>
                    SERVICE
                  </span>

                  <span>
                    {service.number}
                  </span>

                </div>


                <div className="service-visual-ring service-visual-ring-one"></div>

                <div className="service-visual-ring service-visual-ring-two"></div>


                <div className="service-visual-icon">

                  <Icon
                    size={68}
                    strokeWidth={1.2}
                  />

                </div>


                <div className="service-visual-line"></div>


                <div className="service-visual-title">
                  {service.shortTitle}
                </div>


                <div className="service-visual-caption">
                  Practical technology solutions
                </div>


                <div className="service-visual-status">

                  <span></span>

                  SYSTEM READY

                </div>

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          OVERVIEW
      ===================================================== */}

      <section
        ref={overviewRef}
        className={`service-overview-section ${
          visibleSections.overview
            ? "detail-section-visible"
            : ""
        }`}
      >

        <div className="service-section-bg-grid"></div>

        <div className="service-detail-container">

          <div className="service-two-column">

            <div className="detail-section-heading">

              <span className="service-section-label">
                OVERVIEW
              </span>

              <h2>
                Technology designed
                <span>
                  around the problem.
                </span>
              </h2>

            </div>


            <div className="detail-section-content">

              <p className="service-overview-text">
                {service.overview}
              </p>


              <div className="service-scroll-indicator">

                <span className="scroll-indicator-icon">
                  <ArrowDown size={16} />
                </span>

                Explore the service

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          WHAT WE PROVIDE
      ===================================================== */}

      <section
        ref={provideRef}
        className={`service-provide-section ${
          visibleSections.provide
            ? "detail-section-visible"
            : ""
        }`}
      >

        <div className="service-section-bg-grid"></div>

        <div className="service-detail-container">


          <div className="service-heading-row">

            <div className="detail-section-heading">

              <span className="service-section-label">
                WHAT WE PROVIDE
              </span>

              <h2>
                Built for your
                <span>
                  requirements.
                </span>
              </h2>

            </div>


            <p className="detail-section-description">
              Our approach focuses on practical solutions,
              clean implementation and long-term usability.
            </p>

          </div>


          {/* Feature cards */}

          <div className="service-feature-grid">

            {service.services.map(
              (item, index) => (

                <div
                  className={`service-feature-card feature-card-${(
                    index % 4
                  ) + 1}`}
                  key={index}
                >

                  <div className="feature-card-grid"></div>

                  <div className="feature-card-glow"></div>


                  <div className="feature-card-top">

                    <span className="feature-card-number">
                      {String(index + 1).padStart(2, "0")}
                    </span>


                    <CheckCircle2
                      size={21}
                      className="feature-check"
                    />

                  </div>


                  <div className="feature-card-content">

                    <h3>
                      {item.title}
                    </h3>

                    <p>
                      {item.description}
                    </p>

                  </div>


                  <div className="feature-card-line"></div>

                </div>

              )
            )}

          </div>

        </div>

      </section>


      {/* =====================================================
          TECHNOLOGIES
      ===================================================== */}

      <section
        ref={technologyRef}
        className={`service-tech-section ${
          visibleSections.technology
            ? "detail-section-visible"
            : ""
        }`}
      >

        <div className="service-section-bg-grid"></div>

        <div className="service-detail-container">

          <div className="service-two-column tech-layout">


            <div className="detail-section-heading">

              <span className="service-section-label">
                TECHNOLOGY
              </span>

              <h2>
                The right tools for
                <span>
                  the job.
                </span>
              </h2>

            </div>


            <div className="technology-list">

              {service.technologies.map(
                (technology, index) => (

                  <div
                    className="technology-item"
                    key={index}
                  >

                    <span>
                      {String(index + 1).padStart(2, "0")}
                    </span>


                    <strong>
                      {technology}
                    </strong>


                    <ArrowUpRight
                      size={15}
                      className="technology-arrow"
                    />

                  </div>

                )
              )}

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          PROCESS
      ===================================================== */}

      <section
        ref={processRef}
        className={`service-process-section ${
          visibleSections.process
            ? "detail-section-visible"
            : ""
        }`}
      >

        <div className="service-section-bg-grid"></div>

        <div className="service-detail-container">


          <div className="service-heading-row">

            <div className="detail-section-heading">

              <span className="service-section-label">
                OUR PROCESS
              </span>

              <h2>
                From idea to
                <span>
                  implementation.
                </span>
              </h2>

            </div>


            <p className="detail-section-description">
              A structured process keeps development clear,
              efficient and focused on the final outcome.
            </p>

          </div>


          <div className="service-process">

            {service.process.map(
              (step, index) => (

                <div
                  className={`process-step process-step-${(
                    index % 4
                  ) + 1}`}
                  key={index}
                >

                  <span className="process-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>


                  <div className="process-content">

                    <h3>
                      {step}
                    </h3>

                    <p>
                      Nexis Fold focuses on quality,
                      clarity and practical implementation
                      at this stage.
                    </p>

                  </div>


                  <ArrowUpRight
                    size={17}
                    className="process-arrow"
                  />

                </div>

              )
            )}

          </div>

        </div>

      </section>


      {/* =====================================================
          CTA
      ===================================================== */}

      <section
        ref={ctaRef}
        className={`service-detail-cta ${
          visibleSections.cta
            ? "detail-section-visible"
            : ""
        }`}
      >

        <div className="service-detail-container">

          <div className="service-detail-cta-box">

            <div className="service-detail-cta-grid"></div>

            <div className="service-detail-cta-glow"></div>


            <div className="service-detail-cta-content">

              <span className="service-section-label">
                LET'S BUILD TOGETHER
              </span>

              <h2>
                Have a project
                <br />
                <span>
                  in mind?
                </span>
              </h2>

              <p>
                Tell us what you are trying to build.
                We'll help you turn the idea into a
                practical digital solution.
              </p>

            </div>


            <Link
              to="/contact"
              className="service-primary-button"
            >
              Start a Project
              <ArrowUpRight size={17} />
            </Link>

          </div>

        </div>

      </section>

    </div>
  );
}

export default ServiceDetails;